import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { scout, ScoutConversation, ScoutMode } from "./scout-api";

export function useScout({ initialId, agentId, context, account, studio = false }: { initialId?: string; agentId?: string; context?: Record<string, unknown>; account?: string; studio?: boolean }) {
  const { user } = useAuth();
  const identity = `${user?.client_id || ""}:${user?.id || ""}`;
  const storageKey = `scout:${identity}:${studio ? agentId || "draft" : "home"}`;
  const [id, setId] = useState<string | null>(() => {
    if (initialId !== undefined) return initialId || null;
    if (studio && !agentId) return null;
    try { return sessionStorage.getItem(storageKey); } catch { return null; }
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const request = useRef<{ id: string; text: string } | null>(null);
  const creation = useRef(crypto.randomUUID());
  const lock = useRef(false);
  const qc = useQueryClient();
  const select = useCallback((next: string | null) => {
    setId(next); setError(""); request.current = null; creation.current = crypto.randomUUID();
    try { if (next) sessionStorage.setItem(storageKey, next); else sessionStorage.removeItem(storageKey); } catch { /* Server history remains available. */ }
  }, [storageKey]);
  useEffect(() => { if (initialId) select(initialId); }, [initialId, select]);
  const caps = useQuery({ queryKey: ["scout", identity, "capabilities"], queryFn: () => scout<{ analytics: boolean; build: boolean; cross_account: boolean }>("/capabilities"), staleTime: 60000 });
  const conversation = useQuery({
    queryKey: ["scout", identity, "conversation", id],
    queryFn: ({ signal }) => scout<ScoutConversation>(`/conversations/${encodeURIComponent(id!)}`, { signal }),
    enabled: Boolean(id), retry: false,
    refetchInterval: query => ["processing", "starting"].includes(query.state.data?.status || "") || query.state.data?.build?.status === "running" ? 2500 : false,
  });
  const accept = (doc: ScoutConversation) => {
    qc.setQueryData(["scout", identity, "conversation", doc.conversation_id], doc);
    if (id !== doc.conversation_id) { setId(doc.conversation_id); try { sessionStorage.setItem(storageKey, doc.conversation_id); } catch { /* optional cache */ } }
    qc.invalidateQueries({ queryKey: ["scout", identity, "history"] });
  };
  const send = async (text: string, mode: ScoutMode = studio ? "build" : "auto") => {
    if (!text.trim() || lock.current) return;
    lock.current = true; setSending(true); setError("");
    if (!request.current || request.current.text !== text) request.current = { id: crypto.randomUUID(), text };
    const messageId = request.current.id;
    try {
      let conversationId = id;
      if (!conversationId) {
        const created = await scout<ScoutConversation>("/conversations", { method: "POST", body: JSON.stringify({ request_id: creation.current, mode, account_id: account === "all" ? null : account || null, agent_id: agentId || null, context: context || {} }) });
        conversationId = created.conversation_id; accept(created);
      }
      const doc = await scout<ScoutConversation>(`/conversations/${conversationId}/messages`, { method: "POST", body: JSON.stringify({ text, client_message_id: messageId }) });
      accept(doc);
      request.current = null;
      return true;
    } catch (e) { setError((e as Error).message); return false; }
    finally { lock.current = false; setSending(false); }
  };
  const startBuild = async () => {
    if (!id || lock.current) return;
    lock.current = true; setSending(true); setError("");
    try { accept(await scout<ScoutConversation>(`/conversations/${id}/build`, { method: "POST" })); }
    catch (e) { setError((e as Error).message); }
    finally { lock.current = false; setSending(false); }
  };
  const retry = () => {
    const last = [...(conversation.data?.messages || [])].reverse().find(m => m.role === "user");
    if (last) { request.current = { id: last.id, text: last.text }; return send(last.text); }
  };
  return { id, identity, select, conversation, caps, send, sending, error, startBuild, retry };
}
